<script setup>
import {computed, onMounted, ref} from "vue";
import _, {debounce} from "lodash";
import {usePiniaStore} from "../store/pinia_store";

const props = defineProps({
  eventBus: {
    type: Object,
    required: true,
  },
  dentaidApiInstance: {
    type: Function,
    required: true,
  },
  crmId: {
    type: String,
    required: true,
  },
  cartEventBus: {
    type: Object,
    required: true,
  },
  brandTitle: {
    type: String,
    required: true,
  },
  productTitle: {
    type: String,
    required: true,
  },
  productId: {
    type: String,
    required: true,
  },
  minimumOrderQuantity: {
    type: Number,
    required: true,
  }
})

const piniaStore = usePiniaStore();

const getInitialQuantity = computed(() => {
  const cartLine = _.filter(piniaStore.cart.lines, function (cartLine) {
    return cartLine.sku === props.crmId;
  })

  return cartLine?.quantity;
})

const quantityObject = ref({
  quantity: props.minimumOrderQuantity,
  crmId: props.crmId,
});

const isLoading = ref(false);
const addToCartText = window.Drupal.t('Add to cart');
const addToCartMessage = window.Drupal.t('Product successfully added to cart!');
const addToCartMessageError = window.Drupal.t('Something went wrong when adding a product to the cart.');

const handleAddToCart = debounce(() => {
  isLoading.value = true;

  props.dentaidApiInstance.post(`/cart?token=${piniaStore.parsedToken}`, {
    items: {
      "0": {
        sku: props.crmId,
        quantity: quantityObject.value.quantity,
      }
    }
  })
    .then(response => {
      Vue.$toast.success(addToCartMessage);

      piniaStore.updateUserData(response.data.data);

      pushToDataLayer();
    })
    .catch(error => {
      Vue.$toast.error(addToCartMessageError);
    })
    .finally(() => {
      isLoading.value = false;
    });
}, 250)

const formatPriceForDataLayer = (priceInCents) => {
  return (priceInCents / 100).toFixed(2);
}

const calculateUnitDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const quantity = product.quantity || 1;
  const discountPerUnit = (totalUnitPrice - totalNetPrice) / quantity;
  return formatPriceForDataLayer(discountPerUnit);
}

const calculateTotalProductDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const totalDiscount = totalUnitPrice - totalNetPrice;
  return formatPriceForDataLayer(totalDiscount);
}

const getAppliedVoucherCodes = () => {
  const voucherLines = _.filter(piniaStore.cart.lines, (line) => line.type === 'Voucher');
  const voucherCodes = _.map(voucherLines, (voucher) => voucher.code || voucher.name);
  return voucherCodes.join(', ');
}

const pushToDataLayer = () => {
  // Get the product from the cart to access all properties including price and discounts
  const cartProduct = _.find(piniaStore.cart.lines, (line) => line.sku === props.crmId && line.type === 'Product');

  if (!cartProduct) {
    // Fallback to basic data if product not found in cart yet
    window.dataLayer.push({ecommerce: null});
    window.dataLayer.push({
      'event': 'addToCart',
      'ecommerce': {
        'currencyCode': 'EUR',
        'add': {
          'products': [{
            'name': props.productTitle?.toLowerCase(),
            'id': props.crmId,
            'brand': props.brandTitle,
            'quantity': quantityObject.value.quantity
          }]
        }
      }
    });
    return;
  }

  const appliedCoupons = getAppliedVoucherCodes();
  const totalProductPrice = formatPriceForDataLayer(cartProduct.unit_price * cartProduct.quantity);
  const totalProductDiscount = calculateTotalProductDiscount(cartProduct);

  window.dataLayer.push({ecommerce: null});

  window.dataLayer.push({
    'event': 'addToCart',
    'ecommerce': {
      'currencyCode': 'EUR',
      'add': {
        'products': [{
          'name': props.productTitle?.toLowerCase(),
          'id': props.crmId,
          'brand': props.brandTitle,
          'price': formatPriceForDataLayer(cartProduct.unit_price),
          'quantity': cartProduct.quantity,
          'discount': calculateUnitDiscount(cartProduct),
          'total_product_price': (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
          'total_product_discount': totalProductDiscount,
          ...(appliedCoupons && { 'coupon': appliedCoupons })
        }]
      }
    }
  });
}

onMounted(() => {
  props.eventBus.$on('quantity-changed', (value) => quantityObject.value = value);
  props.eventBus.$on('set-quantity', (value) => {
    if (quantityObject.value.crmId === props.crmId) {
      quantityObject.value = value
    }
  });

})
</script>

<template>
  <div class="vue-add-to-cart">
    <button
      :disabled="isLoading"
      class="btn-primary add-to-cart"
      @click="handleAddToCart">
      {{ addToCartText }}
      <template v-if="isLoading">
        <dentaid-loader></dentaid-loader>
      </template>
    </button>
  </div>
</template>
